/**
 * Chat sound effects (Feb 2026).
 *
 * We synthesize an iOS "Glass"-style chime with the Web Audio API instead of
 * shipping an MP3, so we don't need to host an audio asset or worry about
 * CORS / autoplay-policy blocking a fetch. Two quick sine chirps — a rising
 * pair (E5 → G5) with sharp attack and short exponential decay give a
 * bright, glassy feel very close to Apple's default alert tone.
 *
 * `playChime()` respects a persisted mute preference (localStorage key
 * `chat.muted` = "1"). All errors swallowed — audio is a nice-to-have,
 * never critical.
 */

const MUTE_KEY = 'chat.muted';

let _ctx = null;
function ctx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  try {
    _ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    _ctx = null;
  }
  return _ctx;
}

export function isChatMuted() {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setChatMuted(muted) {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function chirp(ac, freq, startAt, duration = 0.18, gain = 0.25) {
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(g).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

export function playChime() {
  if (isChatMuted()) return;
  const ac = ctx();
  if (!ac) return;
  try {
    // Some browsers suspend AudioContext until user interaction. Resume best-effort.
    if (ac.state === 'suspended') ac.resume().catch(() => {});
    const t = ac.currentTime;
    // Two-note glass ping: E5 (659.25 Hz) → G5 (783.99 Hz), 60 ms apart
    chirp(ac, 659.25, t, 0.18, 0.22);
    chirp(ac, 783.99, t + 0.06, 0.22, 0.18);
  } catch {
    /* ignore audio errors */
  }
}
